import {
  InputFieldsType,
  designTypeFieldsRegisterPage,
  orderedRegisterPageFields,
} from '@composites/InboundButton/utils';
import { getLanguage } from '@store/slices/configSlice';
import { printPdf, useMobileWidth } from '@utils/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from '@components/Button';
import Spinner from '@components/Spinner';
import { DefaultBooleanValues } from '@composites/InboundButton';
import { getEventDetails } from '@store/slices/eventSlice';
import {
  ACCOUNT_ID,
  ROOT_URL,
  useGetPublicEventAdvertisement,
  useGetMergedGroupsData,
  usePublicAllAgendas,
  usePublicAttendees,
  usePublicCoLocatedEventsByEventId,
  usePublicExhibitions,
  usePublicPortfolioByAccount,
} from '@utils/apiHooks';

import { groupAndSortByDate, safeJSONParse, uniqBy, getUrl } from '@utils/fn';
import { useInbound } from '@utils/Inbound/useInbound';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  PublicExhibitionItem,
  PublicSessionItem,
} from '@hubs101/booking-api-skd-client/lib/types';
import BusinessPartnerPdfContent from '@composites/PDF/BusinessPartnerPdfContent';
import { getUniquePdfDays } from '@utils/time';
import { agendaPdfDays } from '@pages/Agenda/helper';
import { Ticket } from '@composites/Tickets';

interface DownloadPartnerProspectusProps {
  label: string;
  buttonType: string;
  eventId?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
}

const PARTNER_CATEGORY_ORDER: string[] = [
  'partner_elite',
  'partner_eventlead',
  'partner_competence',
  'partner_business',
  'partner_exhibitor',
  'partner_startups',
  'partner_media',
];

const getPartnerCategoryOrder = (category?: string) => {
  const idx = PARTNER_CATEGORY_ORDER.indexOf(category ?? '');
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

type ExhibitionWithCategory = PublicExhibitionItem & { category?: string };

const DownloadPartnerProspectus = ({
  label,
  buttonType,
  inboundValues,
  eventId,
}: DownloadPartnerProspectusProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const language = useSelector(getLanguage);
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [showModal, setShowModal] = useState<boolean>(false);
  const [checkedApproval, setCheckedApproval] = useState<boolean>(false);
  const { inboundValue, actionInbound, registerInbound, shouldShowForm } =
    useInbound();
  const [forceEnable, setForceEnable] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [eventSeries, setEventSeries] = useState([]);
  const [isEventSeriesLoaded, setEventSeriesLoaded] = useState<boolean>(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [allTickets, setAllTickets] = useState<Ticket[]>([]);

  const getSessionEventId = (s: {
    event?: string | { id?: string; event_id?: string; eventId?: string };
  }): string | undefined => {
    const e = s?.event;
    if (!e) return undefined;
    if (typeof e === 'string') return e;
    return e.id ?? e.event_id ?? e.eventId;
  };

  const { data: attendees } = usePublicAttendees({
    eventId: sluggedEvent?.id,
    enabled: sluggedEvent?.id?.length > 0,
    colocated: sluggedEvent?.colocated,
  });

  const { data: portfolios } = usePublicPortfolioByAccount({
    accountId: ACCOUNT_ID,
  });

  const { data: advertisements = [] } = useGetPublicEventAdvertisement({
    eventId: sluggedEvent?.id || '',
  });

  const {
    isSuccess,
    data: coLocatedEvents,
    isLoading: isCoLocatedEventsLoading,
  } = usePublicCoLocatedEventsByEventId({
    eventId: sluggedEvent?.id ?? '',
    enabled: (sluggedEvent?.id?.length ?? 0) > 0 && !!sluggedEvent?.colocated,
  });

  const selectedEventsIds = useMemo(() => {
    if (sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated)
      return coLocatedEvents?.map(e => e.id) ?? ([] as string[]);
    return [sluggedEvent?.id];
  }, [coLocatedEvents, sluggedEvent?.colocated, sluggedEvent?.id]);

  const agendas: any =
    usePublicAllAgendas({
      page: 0,
      language: sluggedEvent?.language,
      eventId: sluggedEvent?.id,
      enabled: sluggedEvent?.id?.length > 0,
      colocated: sluggedEvent?.colocated,
    }) || [];
  const agendasSessions = useMemo(() => {
    const raw = agendas?.data;

    const sessions: any[] = Array.isArray(raw)
      ? raw.map((e: any) => e?.sessions).flat()
      : (raw?.sessions ?? []);

    return sessions
      .filter(e => Boolean(e))
      .map(e => ({
        ...e,
      }));
  }, [agendas?.data]);

  const sessionsForPdf = useMemo(() => {
    if (sluggedEvent?.colocated) return agendasSessions;

    const currentEventId = sluggedEvent?.id;
    if (!currentEventId) return agendasSessions;

    const mine = agendasSessions.filter(
      s => getSessionEventId(s) === currentEventId
    );

    return mine.length > 0 ? mine : agendasSessions;
  }, [agendasSessions, sluggedEvent?.colocated, sluggedEvent?.id]);

  const agendaPdfDates = useMemo(() => {
    const agendaDaysPdfFormat = getUniquePdfDays(
      sessionsForPdf?.map(e => e?.start_date) ?? [],
      sluggedEvent?.language
    );
    const result = agendaDaysPdfFormat?.map((e, index) => {
      return agendaDaysPdfFormat?.length === 1
        ? agendaDaysPdfFormat[index]
        : `${t(agendaPdfDays[index])} - ${agendaDaysPdfFormat[index]}`;
    });
    return result;
  }, [sessionsForPdf, sluggedEvent?.language, t]);

  const groupedAgendasSessions = useMemo(
    () => Object.values(groupAndSortByDate(sessionsForPdf, 'start_date')),
    [sessionsForPdf]
  );

  const agendasSpeakers = useMemo(() => {
    return uniqBy(
      sessionsForPdf
        ?.map(e => e?.speakers)
        ?.flat()
        ?.filter(notNull => notNull),
      'profile'
    );
  }, [sessionsForPdf]);

  const exhibitions = usePublicExhibitions({
    page: 0,
    language: sluggedEvent?.language,
    eventIds: selectedEventsIds,
    enabled:
      selectedEventsIds?.length > 0 ||
      (sluggedEvent?.colocated && isSuccess && selectedEventsIds?.length > 0),
  });

  const partnerEventIds = useMemo(
    () => (sluggedEvent?.id ? [sluggedEvent.id] : []),
    [sluggedEvent?.id]
  );

  const groupedPartnerItemsData = useGetMergedGroupsData({
    eventIds: partnerEventIds,
    categories: PARTNER_CATEGORY_ORDER,
  }).data;

  const groupedPartnerItems = useMemo(
    () => groupedPartnerItemsData ?? [],
    [groupedPartnerItemsData]
  );

  const allExhibitions = useMemo(() => {
    if (Array.isArray(groupedPartnerItems) && groupedPartnerItems.length > 0) {
      return groupedPartnerItems as PublicExhibitionItem[];
    }

    const items = (exhibitions
      ?.map(a => a?.data)
      ?.flat()
      ?.filter(notNull => notNull) ?? []) as PublicExhibitionItem[];

    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const aCategory = (a.item as ExhibitionWithCategory)?.category;
        const bCategory = (b.item as ExhibitionWithCategory)?.category;
        const byCategory =
          getPartnerCategoryOrder(aCategory) -
          getPartnerCategoryOrder(bCategory);
        return byCategory !== 0 ? byCategory : a.index - b.index;
      })
      .map(({ item }) => item);
  }, [groupedPartnerItems, exhibitions]);

  const isExhibitionsLoading =
    exhibitions?.some(exhibition => exhibition?.isLoading) ?? false;

  useEffect(() => {
    if (sluggedEvent?.id) {
      setEventSeriesLoaded(false);

      fetch(`${ROOT_URL}/tickets?event=${sluggedEvent?.id}`)
        .then(async response => await response.json())
        .then(data => data.result.data)
        .then(ticketsData => {
          const relevantTickets = ticketsData
            .filter(
              (ticket: Ticket) =>
                ticket.visibility === 'public' && ticket.attendee_type === 'de'
            )
            .sort((a: Ticket, b: Ticket) => a.priority - b.priority);

          setTickets(relevantTickets);
          setAllTickets(ticketsData);
        })
        .catch(err => {
          console.error('error = ', err);
        });

      fetch(
        `${ROOT_URL}/accounts/${sluggedEvent?.account}/portfolios/${sluggedEvent?.portfolio?.slug}/events?limit=100&start_date=>${new Date().toISOString().split('T')[0]}`
      )
        .then(async response => await response.json())
        .then(data => data.result.data)
        .then((data: any) => {
          setEventSeries(data);
          setEventSeriesLoaded(true);
        })
        .catch(err => {
          setEventSeriesLoaded(true);
          console.error('error = ', err);
        });
    }
  }, [sluggedEvent]);

  const generatePartnerProspectusPDF = useCallback(() => {
    const doc = (
      <BusinessPartnerPdfContent
        event={sluggedEvent}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        theme={theme}
        groupedAgendasSessions={groupedAgendasSessions}
        agendaPdfDates={agendaPdfDates}
        agendaSpeakers={agendasSpeakers}
        exhibitions={allExhibitions}
        advertisements={advertisements}
        attendees={attendees?.attendees}
        tickets={tickets}
        allTickets={allTickets}
        eventSeries={eventSeries}
      />
    );
    const eventNameWithoutYear = (sluggedEvent?.name ?? '')
      .replace(/\b(19|20)\d{2}\b/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const eventNameForFile = eventNameWithoutYear || (sluggedEvent?.name ?? '');

    setLoading(true);
    printPdf(doc, `${t('sponsorship-prospectus')}-${eventNameForFile}`)
      .catch(console.error)
      .finally(() => {
        setLoading(false);
      });
  }, [
    sluggedEvent,
    coLocatedEvents,
    portfolios,
    groupedAgendasSessions,
    agendasSpeakers,
    allExhibitions,
    advertisements,
    attendees,
    tickets,
    allTickets,
    eventSeries,
    agendaPdfDates,
    language,
    t,
    theme,
  ]);

  const inputElements = useMemo(
    () =>
      orderedRegisterPageFields
        .map(item => {
          if (inboundValues?.booleanValues?.[item]?.visible) {
            const designTypeParameters = designTypeFieldsRegisterPage?.find(
              e => e.id === item
            );
            return {
              ...designTypeParameters,
              ...inboundValues?.booleanValues?.[item],
            };
          }
          return undefined;
        })
        ?.filter(item => item) as InputFieldsType,
    [inboundValues?.booleanValues]
  );

  const defaultValues: Record<string, string> = useMemo(() => {
    return inputElements.reduce<Record<string, string>>((acc, key) => {
      acc[key.id] = safeJSONParse(inboundValue)?.[key.id] ?? '';
      return acc;
    }, {});
  }, [inboundValue, inputElements]);

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Clear error for a field when user starts typing in it
  const handleChange = (fieldName: string) => (value: string) => {
    clearErrors(fieldName);
  };

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      if (!checkedApproval) return;

      await registerInbound(
        {
          ...data,
          event: sluggedEvent?.id,
          path: window.location.href,
          action: 'download_bp_packages',
        },
        () => {
          generatePartnerProspectusPDF();
          setShowModal(false);
        }
      );
    },
    [checkedApproval, inboundValue, registerInbound, sluggedEvent?.id]
  );

  useEffect(() => {
    if (!forceEnable) {
      timerRef.current = setTimeout(() => {
        setForceEnable(true);
      }, 5000);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [forceEnable]);

  const isReady =
    (!!sluggedEvent &&
      !!agendas?.data &&
      Array.isArray(allExhibitions) &&
      !loading &&
      !isEventSeriesLoaded &&
      !isCoLocatedEventsLoading &&
      !agendas?.isLoading &&
      !isExhibitionsLoading) ||
    (forceEnable && !loading);

  return (
    <>
      <Button
        icon={loading ? <Spinner size="2rem" /> : undefined}
        title={label}
        backgroundColor={sluggedEvent?.design?.themeColor}
        hoverBackgroundColor={theme.color.mobilityGrey}
        textColor={theme.color.white}
        lineHeight={theme.lineHeight.height125}
        fontSize={theme.fontSize.size20}
        borderRadius={theme.borderRadius.radius10}
        padding={isMobile ? '1.1rem 2.22rem' : '1.1rem 3rem'}
        width={isMobile ? '100%' : 'max-content'}
        disabled={!isReady}
        onClick={() => {
          void (async () => {
            if (shouldShowForm) {
              // Redirect
              const path = getUrl(t('bp-packages-url'));
              const href = `${window.location.origin}/events/${sluggedEvent?.slug}/${path}`;
              window.location.href = href;
              return;
            }

            // Has inbound value => download + inbound
            generatePartnerProspectusPDF();
            await actionInbound({
              ...JSON.parse(inboundValue ?? '{}'),
              event: sluggedEvent?.id,
              path: window.location.href,
              action: 'download_bp_packages',
            });
          })();
        }}
      />
    </>
  );
};

export default DownloadPartnerProspectus;
